/**
 * Embed-mode branches for the service layer — SER-5886.
 *
 * LEAF MODULE (imports only the embed-session leaf) so Bun tests can exercise
 * the branches without touching shared singletons (repo failure mode #1).
 *
 * The console's axios factory and the SSE stream helper both call into these
 * three functions; outside /embed routes every one of them is a no-op, so the
 * regular console path is byte-identical to before.
 */
import type { AxiosRequestConfig } from 'axios'
import { type DpopKeyStore, getOrCreateDpopKeyPair } from '../utils/embed-dpop-key'
import { buildDpopProof, DPOP_HEADER, dpopPath } from '../utils/embed-dpop-proof'
import {
  dispatchEmbedSessionExpired,
  getEmbedSession,
  isEmbedMode,
  type REmbedSession,
  toEmbedPath,
} from '../utils/embed-session'

/**
 * The DPoP proof header for one request, or {} when none can be produced.
 *
 * Always sent when a key is available — the console does not know, and does not
 * need to know, which mode hodor is in. hodor ignores proofs while DPoP is off,
 * binds the session on the first one it sees in `optional`, and requires them in
 * `required`.
 *
 * ROLLBACK IS SELF-HEALING, which is why no feature flag gates this. If the
 * console is rolled back after hodor has bound a session, that session's next
 * request 401s — and a 401 already clears the session and asks the host to
 * re-mint (embed-layout), producing a fresh unbound one. The failure resolves
 * itself in one round trip rather than stranding the iframe.
 *
 * Never throws: a browser that blocks IndexedDB simply cannot produce a proof,
 * and that must surface as hodor's 401 (with its existing expiry handling)
 * rather than as an exception inside an interceptor.
 *
 * `store` is the same injected seam getOrCreateDpopKeyPair already exposes.
 * `indexedDB` is undefined under Bun + happy-dom, so without it every test of a
 * proofed request asserts on the {} fallback and would stay green with the proof
 * deleted — the exact false-green this parameter exists to prevent. Production
 * never passes it.
 */
export const embedDpopHeaders = async (
  method: string,
  url: string,
  session: REmbedSession | null,
  store?: DpopKeyStore,
): Promise<Record<string, string>> => {
  const bearer = session?.session_token
  if (!bearer) {
    return {}
  }
  try {
    const keyPair = await getOrCreateDpopKeyPair(store)
    if (!keyPair) {
      return {}
    }
    return {
      [DPOP_HEADER]: await buildDpopProof(keyPair, {
        method,
        path: dpopPath(url),
        bearer,
      }),
    }
  } catch {
    return {}
  }
}

/** The authoritative embed identity headers — auth token, client-type wall,
 * and region. Deliberately NO Aom_uuid and NO Browser-Session-Id — an embed
 * session has neither, and hodor's embed middleware strips identity headers
 * anyway. `Client-Type: embed_app` is the hard wall: hodor rejects embed
 * tokens on console routes and console tokens on embed routes by this pair.
 * With no session the Authorization value is an empty `Token ` — deliberate:
 * hodor answers 401 and the session-expired overlay takes over, which is the
 * correct fail-safe for "in embed with no session". */
const embedIdentityHeaders = (session: REmbedSession | null): Record<string, string> => ({
  'Client-Type': 'embed_app',
  Authorization: `Token ${session?.session_token ?? ''}`,
  Region: session?.region ?? '',
})

/**
 * The ONE home of the embed header-precedence wall:
 *   defaults (Content-Type) → call-site overrides → identity headers LAST.
 * A call site may override the Content-Type default (e.g. a multipart upload
 * like voice clone) but can NEVER override the identity wall — do not append
 * anything after the identity spread.
 * `session` defaults to the stored one (interceptor path); callers that
 * already hold a session — e.g. the bootstrap readiness probe before render —
 * pass it explicitly so they stay pure and don't depend on sessionStorage.
 */
export const createEmbedHeaders = (
  session: REmbedSession | null = getEmbedSession(),
  overrides?: Record<string, string>,
): Record<string, string> => ({
  'Content-Type': 'application/json',
  ...overrides,
  ...embedIdentityHeaders(session),
})

/**
 * The URL axios will ACTUALLY request, joined the way axios itself joins it.
 *
 * `${baseURL}${url}` is not that URL, and the difference is a 401 on every
 * affected route. The regional base carries a trailing slash
 * (`https://dev-{{region}}-auth-api.contactodev.com/`) while the call sites that
 * carry their own `/v{n}/` segment pass an absolute path, so concatenation
 * yields `https://host//v1/embed/…`. axios collapses that to ONE slash before
 * sending, so hodor receives `/v1/embed/…` while a proof built over the
 * concatenation claims `htu: "//v1/embed/…"`. hodor compares `htu` against its
 * own request path and refuses the mismatch with 401 "Invalid proof of
 * possession" (verified live 2026-08-12 against dev: same session and route,
 * single-slash htu → 200, double-slash htu → 401).
 *
 * That 401 is far worse than one dead request: in embed a 401 means "session
 * expired", so it tears the iframe down and the host re-mints — which walks the
 * account into hodor's per-account session cap and locks it out. rag-service
 * survived only because it is on the resource-scoped exemption list; ai-assist
 * is not, so opening the agent builder was enough to start the cycle.
 *
 * Mirrors axios 0.21.4 buildFullPath + combineURLs EXACTLY (an absolute url wins
 * outright; otherwise strip trailing slashes from the base and leading slashes
 * from the relative, then join with exactly one) so the two can never drift.
 * Exported for unit testing.
 */
export const embedProofUrl = (baseURL: string, url: string): string => {
  if (!baseURL || /^([a-z][a-z\d+\-.]*:)?\/\//i.test(url)) {
    return url || baseURL
  }
  return url ? `${baseURL.replace(/\/+$/, '')}/${url.replace(/^\/+/, '')}` : baseURL
}

/**
 * Request branch: rewrite the URL onto hodor's /v1/embed gateway and resolve
 * the {{region}} placeholder from the embed session (there is no
 * localStorage['auth'] to read a region from in an iframe).
 * Returns the config untouched outside embed mode.
 */
export const applyEmbedRequestConfig = async (
  config: AxiosRequestConfig,
): Promise<AxiosRequestConfig> => {
  if (!isEmbedMode()) {
    return config
  }
  const session = getEmbedSession()
  // Fail closed: with no live session there is no region, and replacing
  // {{region}} with '' would send the call to a malformed regional host whose
  // failure surfaces as a network error, NOT the clean 401 → expiry-overlay
  // path. Only reachable after the session is cleared (post-expiry). Surface
  // expiry and abort the request instead. (The bootstrap's exchange + readiness
  // probe use raw axios, not this interceptor, so they're unaffected.)
  if (!session?.region) {
    dispatchEmbedSessionExpired()
    throw new Error('Embed session expired')
  }
  let baseURL = config.baseURL ?? ''
  let url = config.url ?? ''

  if (baseURL.includes('{{region}}')) {
    baseURL = baseURL.replace('{{region}}', session?.region ?? '')
  }
  // Rewrite whichever part carries the /v{n}/ segment: service baseURLs embed
  // it (`https://host/v1/contacto-core/…`, `/v2/…` for versioned services); a
  // few call sites pass absolute paths (`/v1/ai-assist/…`) with a host-only
  // baseURL. Version-generic to match toEmbedPath (legacy trigger /v2,
  // external-apps /v2|/v3).
  if (/\/v\d+\//.test(url)) {
    url = toEmbedPath(url)
  } else {
    baseURL = toEmbedPath(baseURL)
  }
  // Computed AFTER the rewrite above, never before: `htu` must be the path hodor
  // actually receives. A proof built over the pre-rewrite URL would be a valid
  // signature over the wrong path and would 401 every request. Joined through
  // embedProofUrl for the same reason — see its doc for the double-slash 401.
  const proof = await embedDpopHeaders(
    (config.method ?? 'get').toUpperCase(),
    embedProofUrl(baseURL, url),
    session,
  )
  return {
    ...config,
    baseURL,
    url,
    // Precedence (defaults → call-site → identity wall) lives in ONE place:
    // createEmbedHeaders. The proof rides OUTSIDE that wall deliberately — it is
    // per-request, not identity, and createEmbedHeaders must stay synchronous
    // for the callers that cannot await (see sse-stream).
    headers: {
      ...createEmbedHeaders(session, config.headers as Record<string, string> | undefined),
      ...proof,
    },
  }
}

/**
 * Response-error branch: a 401 in embed mode means the embed session expired
 * or was revoked. The iframe must NEVER run the console logout flow
 * (window.cx.logout wipes localStorage['auth'] — it would log out a real
 * console session in another tab). Instead an event fires for the embed
 * layout to show its overlay and postMessage the parent.
 * Returns true when the 401 was handled here.
 */
export const handleEmbedUnauthorized = (status?: number): boolean => {
  if (!isEmbedMode() || status !== 401) {
    return false
  }
  dispatchEmbedSessionExpired()
  return true
}
